import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import LoginPage from "./components/LoginPage";
import Shell from "./components/Shell";
import { AuthProvider } from "./contexts/AuthContext";
import { AgentProvider } from "./contexts/AgentContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { isAuthenticated, clearAuth, getStoredUser, getStoredTenant, setStoredTenant, authApi } from "./api/client";

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [user, setUser] = useState(getStoredUser());
  const [tenant, setTenant] = useState(getStoredTenant());

  // Detect invite/reset tokens from URL
  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get("invite");
  const resetToken = urlParams.get("reset");

  useEffect(() => {
    const handleLogout = () => {
      setAuthed(false);
      setUser(null);
      setTenant(null);
      // Show session expired message on login page
      sessionStorage.setItem("session_expired", "true");
    };
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, []);

  const handleLogin = (data) => {
    setUser(data.user);
    setTenant(data.tenant);
    setAuthed(true);
    if (inviteToken) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleLogout = () => {
    clearAuth();
    setAuthed(false);
    setUser(null);
    setTenant(null);
  };

  // Re-fetch tenant info (e.g. after a Branding logo upload/removal) so the
  // topbar reflects the change without requiring a re-login.
  const refreshTenant = async () => {
    try {
      const res = await authApi.tenant();
      if (res.success && res.data) {
        const merged = { ...tenant, ...res.data };
        setTenant(merged);
        setStoredTenant(merged);
      }
    } catch {
      // Non-fatal — the topbar just keeps showing the prior state.
    }
  };

  // Show invite/reset form if token present (even if logged in)
  if (inviteToken || resetToken || !authed) {
    return (
      <MotionConfig reducedMotion="user">
        <ThemeProvider>
          <LoginPage onLogin={handleLogin} inviteToken={inviteToken} resetToken={resetToken} />
        </ThemeProvider>
      </MotionConfig>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <ThemeProvider>
        <AuthProvider currentUser={user} currentTenant={tenant} onLogout={handleLogout} refreshTenant={refreshTenant}>
          <AgentProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<Shell />} />
              </Routes>
            </BrowserRouter>
          </AgentProvider>
        </AuthProvider>
      </ThemeProvider>
    </MotionConfig>
  );
}
